import {
  Type,
  List,
  Calendar,
  Clock,
  Users,
  CheckSquare,
  Star,
  Hash,
  MessageSquare,
  BarChart3,
  CircleDot,
  Target,
  FileText
} from 'lucide-react';
import { getCurrentLocale } from '../i18n/format';
import type { Locale } from '../i18n/locale';
import ptGantt from '../i18n/locales/pt/gantt';
import enGantt from '../i18n/locales/en/gantt';
import esGantt from '../i18n/locales/es/gantt';

/**
 * CHAVE NUNCA É RÓTULO.
 *
 * Este arquivo é DADO, não interface: o `id` da coluna vai para
 * `projects.settings.columns` e casa com o campo de `tasks`; o `value` do
 * status é `tasks.status`, que o drop do Kanban compara; a prioridade é o
 * inteiro 0..3 do banco. Nada disso muda de idioma.
 *
 * Só o texto exibido mudou de lugar: `name`, `description` e `label` viraram
 * GETTERS que leem o dicionário `gantt`, indexado por esse mesmo id/value. Como
 * são getters, quem lê (`getStatusInfo(x)?.label`, `resolveStatusInfo`,
 * `COLUMN_TYPES[t].name`) continua com a mesma forma de antes e ganha o idioma
 * corrente de graça — inclusive o Gantt e o Kanban, que importam daqui.
 *
 * Por que os três dicionários entram estáticos em vez do import dinâmico do
 * LocaleProvider: este módulo não é componente e não pode esperar Promise. As
 * arrays são avaliadas no import e lidas por código síncrono (`.map`, `.find`),
 * então um dicionário que chega depois mostraria português na primeira pintura.
 * O custo é o namespace `gantt` das três línguas no chunk do app autenticado,
 * que é onde este arquivo já vive.
 */
const GANTT_COPY: Record<Locale, typeof ptGantt> = {
  'pt-BR': ptGantt,
  en: enGantt,
  es: esGantt,
};

/** Cópia do idioma corrente. O locale é mantido pelo LocaleProvider. */
const t = (): typeof ptGantt => GANTT_COPY[getCurrentLocale()] ?? ptGantt;

// Definição dos tipos de coluna disponíveis
export const COLUMN_TYPES = {
  text: {
    id: 'text',
    get name() { return t().columnTypes.text.name; },
    icon: Type,
    get description() { return t().columnTypes.text.description; },
    editable: true,
    defaultWidth: 150,
    minWidth: 80,
    maxWidth: 400
  },
  select: {
    id: 'select',
    get name() { return t().columnTypes.select.name; },
    icon: List,
    get description() { return t().columnTypes.select.description; },
    editable: true,
    defaultWidth: 150,
    minWidth: 100,
    maxWidth: 300,
    requiresOptions: true
  },
  date: {
    id: 'date',
    get name() { return t().columnTypes.date.name; },
    icon: Calendar,
    get description() { return t().columnTypes.date.description; },
    editable: true,
    defaultWidth: 120,
    minWidth: 100,
    maxWidth: 200,
    canAffectGantt: true
  },
  duration: {
    id: 'duration',
    get name() { return t().columnTypes.duration.name; },
    icon: Clock,
    get description() { return t().columnTypes.duration.description; },
    editable: true,
    defaultWidth: 100,
    minWidth: 80,
    maxWidth: 150
  },
  hours: {
    id: 'hours',
    get name() { return t().columnTypes.hours.name; },
    icon: Clock,
    get description() { return t().columnTypes.hours.description; },
    editable: true,
    defaultWidth: 100,
    minWidth: 70,
    maxWidth: 150
  },
  money: {
    id: 'money',
    get name() { return t().columnTypes.money.name; },
    icon: Clock,
    get description() { return t().columnTypes.money.description; },
    editable: false,
    defaultWidth: 120,
    minWidth: 90,
    maxWidth: 180,
    readonly: true
  },
  people: {
    id: 'people',
    get name() { return t().columnTypes.people.name; },
    icon: Users,
    get description() { return t().columnTypes.people.description; },
    editable: true,
    defaultWidth: 160,
    minWidth: 120,
    maxWidth: 250
  },
  checkbox: {
    id: 'checkbox',
    get name() { return t().columnTypes.checkbox.name; },
    icon: CheckSquare,
    get description() { return t().columnTypes.checkbox.description; },
    editable: true,
    defaultWidth: 100,
    minWidth: 60,
    maxWidth: 150
  },
  symbols: {
    id: 'symbols',
    get name() { return t().columnTypes.symbols.name; },
    icon: Star,
    get description() { return t().columnTypes.symbols.description; },
    editable: true,
    defaultWidth: 100,
    minWidth: 60,
    maxWidth: 150,
    requiresOptions: true
  },
  autonumber: {
    id: 'autonumber',
    get name() { return t().columnTypes.autonumber.name; },
    icon: Hash,
    get description() { return t().columnTypes.autonumber.description; },
    editable: false,
    defaultWidth: 60,
    minWidth: 40,
    maxWidth: 100,
    readonly: true
  },
  comment: {
    id: 'comment',
    get name() { return t().columnTypes.comment.name; },
    icon: MessageSquare,
    get description() { return t().columnTypes.comment.description; },
    editable: true,
    defaultWidth: 200,
    minWidth: 150,
    maxWidth: 400
  },
  progress: {
    id: 'progress',
    get name() { return t().columnTypes.progress.name; },
    icon: BarChart3,
    get description() { return t().columnTypes.progress.description; },
    editable: true,
    defaultWidth: 140,
    minWidth: 100,
    maxWidth: 200
  },
  status: {
    id: 'status',
    get name() { return t().columnTypes.status.name; },
    icon: CircleDot,
    get description() { return t().columnTypes.status.description; },
    editable: true,
    defaultWidth: 130,
    minWidth: 100,
    maxWidth: 180
  },
  sprint: {
    id: 'sprint',
    get name() { return t().columnTypes.sprint.name; },
    icon: Target,
    get description() { return t().columnTypes.sprint.description; },
    editable: true,
    defaultWidth: 140,
    minWidth: 100,
    maxWidth: 200
  },
  description: {
    id: 'description',
    get name() { return t().columnTypes.description.name; },
    icon: FileText,
    get description() { return t().columnTypes.description.description; },
    editable: true,
    defaultWidth: 200,
    minWidth: 100,
    maxWidth: 500
  }
};

// Status options with colors (for system status column).
// `value` é `tasks.status`: valor persistido e comparado no drop do Kanban.
export const TASK_STATUS_OPTIONS = [
  { value: 'not_started', get label() { return t().status.not_started; }, color: '#94a3b8', bgColor: '#f1f5f9', auto: true },
  { value: 'in_progress', get label() { return t().status.in_progress; }, color: '#3b82f6', bgColor: '#eff6ff', auto: true },
  { value: 'completed', get label() { return t().status.completed; }, color: '#22c55e', bgColor: '#f0fdf4', auto: true },
  { value: 'paused', get label() { return t().status.paused; }, color: '#f59e0b', bgColor: '#fffbeb', auto: false },
  { value: 'blocked', get label() { return t().status.blocked; }, color: '#ef4444', bgColor: '#fef2f2', auto: false }
];

// Function to calculate status based on progress
export const calculateTaskStatus = (task: { progress?: number; status?: string | null }): string => {
  // If manual status is set (paused or blocked), respect it
  if (task.status === 'paused' || task.status === 'blocked') {
    return task.status;
  }

  // Auto-calculate based on progress (ensure number conversion)
  const progress = Number(task.progress) || 0;

  if (progress <= 0) return 'not_started';
  if (progress >= 100) return 'completed';
  return 'in_progress';
};

/**
 * Rótulo e cor de um dos CINCO status canônicos, ou `null`.
 *
 * ATENÇÃO ao usar: desde 24/08/2026 a coluna do Kanban é livre, e
 * `tasks.status` guarda o id de uma coluna de `kanban_columns`, que pode ser
 * "publicado" ou qualquer coisa que o cliente tenha criado. Esta função só
 * conhece os cinco canônicos.
 *
 * Para exibir o status de uma TAREFA use `resolveStatusInfo(status, columns)`
 * de `config/kanbanCategories`, que resolve contra o quadro do projeto. A
 * versão anterior desta função devolvia `TASK_STATUS_OPTIONS[0]` para valor
 * desconhecido, e o efeito era todo cartão em coluna customizada aparecer como
 * "Não iniciado" cinza no Gantt, no Calendário, no Cronograma e na tabela. Não
 * quebrava nada: só mentia, que é bem pior de achar.
 */
export const getStatusInfo = (statusValue: string | null | undefined) => {
  return TASK_STATUS_OPTIONS.find(s => s.value === statusValue) || null;
};


// Priority options for system priority column (numeric values).
// `value` é `tasks.priority`: o inteiro gravado. `key` só indexa o rótulo.
export const TASK_PRIORITY_OPTIONS = [
  { value: 0, key: 'none', get label() { return t().priority.none; }, color: '#94a3b8', bgColor: '#f1f5f9' },
  { value: 1, key: 'low', get label() { return t().priority.low; }, color: '#1d4ed8', bgColor: '#dbeafe' },
  { value: 2, key: 'medium', get label() { return t().priority.medium; }, color: '#92400e', bgColor: '#fef3c7' },
  { value: 3, key: 'high', get label() { return t().priority.high; }, color: '#dc2626', bgColor: '#fee2e2' }
];

// Get priority info by numeric value
export const getPriorityInfo = (priorityValue: number | null | undefined) => {
  return TASK_PRIORITY_OPTIONS.find(p => p.value === priorityValue) || TASK_PRIORITY_OPTIONS[0];
};

// Símbolos pré-definidos para o tipo 'symbols'.
// `value` é o que vai para `customFields`; `icon` e `color` são valores também.
//
// ATENÇÃO: `DEFAULT_SYMBOLS.status` NÃO tem relação com `tasks.status` nem com
// as colunas do Kanban. É uma lista de símbolos para COLUNA CUSTOMIZADA da
// tabela, e o fato de repetir quatro dos nomes canônicos é coincidência
// histórica. Quem for atrás de "onde ficam os status" não é aqui: é
// `kanban_columns`, resolvido por `config/kanbanCategories`.
//
// Dívida conhecida e fora do escopo desta entrega: os ícones são EMOJI, que o
// CLAUDE.md proíbe no produto. Trocar por dots CSS mexe no renderizador do
// tipo 'symbols' e no dado já gravado em `customFields` de quem usa a coluna,
// então é entrega própria, não boy-scout.
export const DEFAULT_SYMBOLS = {
  priority: [
    { value: 'critical', get label() { return t().symbols.priority.critical; }, icon: '🔴', color: '#ef4444' },
    { value: 'high', get label() { return t().symbols.priority.high; }, icon: '🟠', color: '#f97316' },
    { value: 'medium', get label() { return t().symbols.priority.medium; }, icon: '🟡', color: '#eab308' },
    { value: 'low', get label() { return t().symbols.priority.low; }, icon: '🟢', color: '#22c55e' },
    { value: 'none', get label() { return t().symbols.priority.none; }, icon: '⚪', color: '#94a3b8' }
  ],
  status: [
    { value: 'not_started', get label() { return t().symbols.status.not_started; }, icon: '⬜', color: '#94a3b8' },
    { value: 'in_progress', get label() { return t().symbols.status.in_progress; }, icon: '🔵', color: '#3b82f6' },
    { value: 'blocked', get label() { return t().symbols.status.blocked; }, icon: '🔴', color: '#ef4444' },
    { value: 'completed', get label() { return t().symbols.status.completed; }, icon: '✅', color: '#22c55e' }
  ],
  flags: [
    { value: 'flag_red', get label() { return t().symbols.flags.flag_red; }, icon: '🚩', color: '#ef4444' },
    { value: 'flag_yellow', get label() { return t().symbols.flags.flag_yellow; }, icon: '🏳️', color: '#eab308' },
    { value: 'flag_green', get label() { return t().symbols.flags.flag_green; }, icon: '🏁', color: '#22c55e' },
    { value: 'none', get label() { return t().symbols.flags.none; }, icon: '—', color: '#94a3b8' }
  ]
};

// Colunas padrão do sistema (não podem ser excluídas, apenas ocultadas).
//
// `visible` aqui é o PADRÃO DE PROJETO NOVO. Projeto existente não se mexe: a escolha dele
// já está gravada em `projects.settings.columns` e o merge de ProjectContext a preserva.
//
// Em 28/08/2026 `description`, `estimated_hours` e `actual_hours` passaram a nascer ocultas.
// Eram 13 colunas visíveis somando ~1.610px, mais largo que uma folha A4 paisagem (1.123px
// a 96dpi) antes da primeira barra do Gantt aparecer: a tela nascia apertada e o PDF saía
// ilegível. Com as três fora sobram 10 colunas e ~1.180px. Quem precisa delas liga no menu
// de colunas, que agora fica no cabeçalho do módulo e não mais atrás do "+".
// O `id` é gravado em `projects.settings.columns` e casa com o campo de
// `tasks`: só o `name` exibido veio do dicionário, indexado por esse mesmo id.
export const DEFAULT_COLUMNS = [
  {
    id: 'rowNumber',
    get name() { return t().columns.rowNumber; },
    type: 'autonumber',
    width: 50,
    minWidth: 40,
    maxWidth: 80,
    position: 0,
    visible: true,
    editable: false,
    deletable: false,
    system: true
  },
  {
    id: 'name',
    get name() { return t().columns.name; },
    type: 'text',
    width: 200,
    minWidth: 150,
    maxWidth: 500,
    position: 1,
    visible: true,
    editable: true,
    deletable: false,
    system: true,
    required: true
  },
  // Balão de conversa (task_comments), padrão Smartsheet/Monday. Position
  // fracionária de propósito: encaixa entre nome (1) e descrição (2) sem
  // renumerar as demais — inclusive nos projetos existentes, cujas positions
  // salvas são inteiras. Não entra em COLUMN_TYPES: é singleton de sistema,
  // o usuário não cria uma segunda. A coluna custom de "anotação" (type
  // 'comment', texto em custom_fields) é OUTRA feature e continua existindo.
  {
    id: 'conversation',
    get name() { return t().columns.conversation; },
    type: 'conversation',
    width: 60,
    minWidth: 48,
    maxWidth: 100,
    position: 1.5,
    visible: true,
    editable: false,
    deletable: false,
    system: true,
    readonly: true
  },
  {
    id: 'description',
    get name() { return t().columns.description; },
    type: 'description',
    width: 200,
    minWidth: 100,
    maxWidth: 500,
    position: 2,
    visible: false,
    editable: true,
    deletable: false,
    system: true
  },
  {
    id: 'predecessors',
    get name() { return t().columns.predecessors; },
    type: 'text',
    width: 100,
    minWidth: 80,
    maxWidth: 200,
    position: 3,
    visible: true,
    editable: true,
    deletable: false,
    system: true
  },
  {
    id: 'duration',
    get name() { return t().columns.duration; },
    type: 'duration',
    width: 100,
    minWidth: 80,
    maxWidth: 150,
    position: 4,
    visible: true,
    editable: true,
    deletable: false,
    system: true
  },
  {
    id: 'startDate',
    get name() { return t().columns.startDate; },
    type: 'date',
    width: 120,
    minWidth: 100,
    maxWidth: 180,
    position: 7,
    visible: true,
    editable: true,
    deletable: false,
    system: true,
    affectsGantt: true,
    ganttField: 'startDate'
  },
  {
    id: 'endDate',
    get name() { return t().columns.endDate; },
    type: 'date',
    width: 120,
    minWidth: 100,
    maxWidth: 180,
    position: 8,
    visible: true,
    editable: true,
    deletable: false,
    system: true,
    affectsGantt: true,
    ganttField: 'endDate'
  },
  {
    id: 'assigned',
    get name() { return t().columns.assigned; },
    type: 'people',
    width: 160,
    minWidth: 120,
    maxWidth: 250,
    position: 9,
    visible: true,
    editable: true,
    deletable: false,
    system: true
  },
  {
    id: 'sprint_id',
    get name() { return t().columns.sprint_id; },
    type: 'sprint',
    width: 140,
    minWidth: 100,
    maxWidth: 200,
    position: 10,
    visible: false,
    editable: true,
    deletable: false,
    system: true
  },
  {
    id: 'priority',
    get name() { return t().columns.priority; },
    type: 'priority',
    width: 120,
    minWidth: 80,
    maxWidth: 150,
    position: 11,
    visible: false,
    editable: true,
    deletable: false,
    system: true,
    affectsGantt: false
  },
  {
    id: 'progress',
    get name() { return t().columns.progress; },
    type: 'progress',
    width: 140,
    minWidth: 100,
    maxWidth: 200,
    position: 12,
    visible: true,
    editable: true,
    deletable: false,
    system: true
  },
  {
    id: 'status',
    get name() { return t().columns.status; },
    type: 'status',
    width: 130,
    minWidth: 100,
    maxWidth: 180,
    position: 13,
    visible: true,
    editable: true,
    deletable: false,
    system: true
  },
  // Colunas de horas (modelo canônico PMBoK/Jira) — editáveis inline no Gantt e Kanban.
  // `estimated_hours` e `actual_hours` ficam imediatamente após `duration` (positions 5/6),
  // pareadas com a duração — é o agrupamento conceitual padrão do PMBoK (work + duration).
  // Default visíveis (alinhado com MS Project Tracking Gantt, Wrike "Effort" + "Time spent",
  // Smartsheet) editaveis direto na grade; nascem OCULTAS desde 28/08/2026 (ver nota do array) e o PM liga no menu de colunas.
  // `remaining_hours`, `baseline_estimated_hours`, `hours_variance` seguem ocultas — opt-in.
  {
    id: 'estimated_hours',
    get name() { return t().columns.estimated_hours; },
    type: 'hours',
    width: 100,
    minWidth: 70,
    maxWidth: 150,
    position: 5,
    visible: false,
    editable: true,
    deletable: false,
    system: true
  },
  {
    id: 'actual_hours',
    get name() { return t().columns.actual_hours; },
    type: 'hours',
    width: 100,
    minWidth: 70,
    maxWidth: 150,
    position: 6,
    visible: false,
    editable: true,
    deletable: false,
    system: true
  },
  {
    id: 'remaining_hours',
    get name() { return t().columns.remaining_hours; },
    type: 'hours',
    width: 100,
    minWidth: 70,
    maxWidth: 150,
    position: 14,
    visible: false,
    editable: true,
    deletable: false,
    system: true
  },
  {
    id: 'baseline_estimated_hours',
    get name() { return t().columns.baseline_estimated_hours; },
    type: 'hours',
    width: 120,
    minWidth: 90,
    maxWidth: 180,
    position: 15,
    visible: false,
    editable: false,
    deletable: false,
    system: true,
    readonly: true
  },
  {
    id: 'hours_variance',
    get name() { return t().columns.hours_variance; },
    type: 'hours',
    width: 100,
    minWidth: 70,
    maxWidth: 150,
    position: 16,
    visible: false,
    editable: false,
    deletable: false,
    system: true,
    readonly: true,
    computed: true  // derivado: estimated_hours - baseline_estimated_hours
  },
  {
    id: 'planned_cost',
    get name() { return t().columns.planned_cost; },
    type: 'money',
    width: 120,
    minWidth: 90,
    maxWidth: 180,
    position: 17,
    visible: false,
    editable: false,
    deletable: false,
    system: true,
    readonly: true,
    computed: true  // derivado: estimated_hours × resource.hourly_rate
  },
  {
    id: 'actual_cost',
    get name() { return t().columns.actual_cost; },
    type: 'money',
    width: 120,
    minWidth: 90,
    maxWidth: 180,
    position: 18,
    visible: false,
    editable: false,
    deletable: false,
    system: true,
    readonly: true,
    computed: true  // derivado: actual_hours × resource.hourly_rate
  }
];

// Função para criar uma nova coluna com valores padrão
export function createColumn(type: string, name: string, options: Record<string, unknown> = {}) {
  const typeConfig = COLUMN_TYPES[type as keyof typeof COLUMN_TYPES];
  if (!typeConfig) {
    throw new Error(`Tipo de coluna inválido: ${type}`);
  }

  return {
    id: `col_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
    name: name || typeConfig.name,
    type: type,
    width: (options.width as number) || typeConfig.defaultWidth,
    minWidth: typeConfig.minWidth,
    maxWidth: typeConfig.maxWidth,
    position: (options.position as number) || 999,
    visible: true,
    editable: typeConfig.editable,
    deletable: true,
    system: false,
    required: (options.required as boolean) || false,
    defaultValue: (options.defaultValue as unknown) || null,
    options: (options.options as unknown[]) || [],
    affectsGantt: (options.affectsGantt as boolean) || false,
    ganttField: (options.ganttField as string) || null
  };
}

// Função para obter o valor padrão de um tipo
export function getDefaultValue(type: string) {
  switch (type) {
    case 'text':
    case 'duration':
    case 'comment':
      return '';
    case 'select':
    case 'people':
    case 'symbols':
    case 'date':
    case 'sprint':
      return null;
    case 'checkbox':
      return false;
    case 'autonumber':
    case 'progress':
      return 0;
    case 'status':
      return null; // Will be auto-calculated
    default:
      return null;
  }
}

// Função para validar valor baseado no tipo
export function validateValue(type: string, value: unknown) {
  switch (type) {
    case 'text':
    case 'comment':
      return typeof value === 'string' || value === null;
    case 'duration':
      return typeof value === 'string' && /^(\d+[dwmh]?\s*)+$/i.test(value) || value === '';
    case 'date':
      return value instanceof Date || typeof value === 'string' || value === null;
    case 'select':
    case 'people':
    case 'symbols':
    case 'status':
    case 'sprint':
      return typeof value === 'string' || value === null;
    case 'checkbox':
      return typeof value === 'boolean';
    case 'autonumber':
    case 'progress':
      return typeof value === 'number';
    default:
      return true;
  }
}

// Lista de tipos para exibição no modal de criação
export const COLUMN_TYPE_LIST = Object.values(COLUMN_TYPES);
